/**
 * GameSettings - In-game settings menu
 * 
 * Sections:
 * - Camera (mode, sensitivity, invert Y)
 * - Graphics (quality profile, show performance stats)
 * - Audio (master volume, voice volume, mic settings)
 * - Controls (show tutorial)
 */

import { useState, useEffect } from 'react';
import { X, Camera, Monitor, Volume2, Gamepad2, ChevronRight } from 'lucide-react';

// Camera modes
export const CAMERA_MODES = {
  firstPerson: { label: 'First Person', distance: 0, height: 1.7 },
  thirdPersonNear: { label: 'Third Person Near', distance: 3, height: 2 },
  thirdPersonNormal: { label: 'Third Person Normal', distance: 5, height: 3 },
  thirdPersonFar: { label: 'Third Person Far', distance: 8, height: 4 },
};

// Default settings
const DEFAULT_SETTINGS = {
  cameraMode: 'thirdPersonNormal',
  cameraSensitivity: 1.0,
  invertY: false,
  graphicsProfile: 'auto',
  showPerfStats: false,
  masterVolume: 1.0,
  voiceVolume: 1.0,
  voiceInputMode: 'pushToTalk', // 'pushToTalk' or 'voiceActivation'
  voiceActivationSensitivity: 0.5,
  micMuted: false,
};

// Load settings from localStorage
export function loadSettings() {
  const saved = localStorage.getItem('midgard_settings');
  if (saved) {
    try {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
    } catch {
      return DEFAULT_SETTINGS;
    }
  }
  return DEFAULT_SETTINGS;
}

// Save settings to localStorage
export function saveSettings(settings) {
  localStorage.setItem('midgard_settings', JSON.stringify(settings));
  // Also save individual keys for backward compatibility
  localStorage.setItem('midgard_graphics_profile', settings.graphicsProfile);
  localStorage.setItem('midgard_show_perf_stats', settings.showPerfStats.toString());
}

function SettingsSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-amber-400" />
          <span className="font-medium">{title}</span>
        </div>
        <ChevronRight className={`w-5 h-5 text-white/50 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

function SettingRow({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/70 text-sm">{label}</span>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, ...props }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-black/60 border border-white/20 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500"
      {...props}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function Slider({ value, onChange, min = 0, max = 1, step = 0.1 }) {
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-32 accent-amber-500"
    />
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`w-12 h-6 rounded-full transition-colors ${checked ? 'bg-amber-500' : 'bg-white/20'}`}
    >
      <div
        className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

export default function GameSettings({ 
  isOpen, 
  onClose, 
  settings, 
  onSettingsChange,
  onShowTutorial,
}) {
  const [localSettings, setLocalSettings] = useState(settings);
  
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);
  
  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  
  const handleChange = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    saveSettings(newSettings);
    onSettingsChange(newSettings);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="settings-modal">
      <div className="w-full max-w-md bg-gray-900/95 rounded-xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="settings-close-btn"
          >
            <X className="w-5 h-5 text-white/70" />
          </button>
        </div>
        
        {/* Settings sections */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Camera */}
          <SettingsSection title="Camera" icon={Camera} defaultOpen>
            <SettingRow label="Camera Mode">
              <Select
                value={localSettings.cameraMode}
                onChange={(v) => handleChange('cameraMode', v)}
                options={[
                  { value: 'firstPerson', label: 'First Person' },
                  { value: 'thirdPersonNear', label: 'Third Person Near' },
                  { value: 'thirdPersonNormal', label: 'Third Person Normal' },
                  { value: 'thirdPersonFar', label: 'Third Person Far' },
                ]}
                data-testid="setting-camera-mode"
              />
            </SettingRow>
            <SettingRow label="Sensitivity">
              <div className="flex items-center gap-2">
                <Slider
                  value={localSettings.cameraSensitivity}
                  onChange={(v) => handleChange('cameraSensitivity', v)}
                  min={0.2}
                  max={2.0}
                  step={0.1}
                />
                <span className="text-xs text-white/50 w-8">{localSettings.cameraSensitivity.toFixed(1)}</span>
              </div>
            </SettingRow>
            <SettingRow label="Invert Y">
              <Toggle
                checked={localSettings.invertY}
                onChange={(v) => handleChange('invertY', v)}
              />
            </SettingRow>
          </SettingsSection>
          
          {/* Graphics */}
          <SettingsSection title="Graphics" icon={Monitor}>
            <SettingRow label="Quality">
              <Select
                value={localSettings.graphicsProfile}
                onChange={(v) => handleChange('graphicsProfile', v)}
                options={[
                  { value: 'auto', label: 'Auto' },
                  { value: 'performance', label: 'Performance' },
                  { value: 'balanced', label: 'Balanced' },
                  { value: 'quality', label: 'Quality' },
                ]}
              />
            </SettingRow>
            <SettingRow label="Show Performance Stats">
              <Toggle
                checked={localSettings.showPerfStats}
                onChange={(v) => handleChange('showPerfStats', v)}
              />
            </SettingRow>
          </SettingsSection>
          
          {/* Audio */}
          <SettingsSection title="Audio" icon={Volume2}>
            <SettingRow label="Master Volume">
              <div className="flex items-center gap-2">
                <Slider
                  value={localSettings.masterVolume}
                  onChange={(v) => handleChange('masterVolume', v)}
                />
                <span className="text-xs text-white/50 w-8">{Math.round(localSettings.masterVolume * 100)}%</span>
              </div>
            </SettingRow>
            <SettingRow label="Voice Volume">
              <div className="flex items-center gap-2">
                <Slider
                  value={localSettings.voiceVolume}
                  onChange={(v) => handleChange('voiceVolume', v)}
                />
                <span className="text-xs text-white/50 w-8">{Math.round(localSettings.voiceVolume * 100)}%</span>
              </div>
            </SettingRow>
            <SettingRow label="Voice Input Mode">
              <Select 
                value={localSettings.voiceInputMode} 
                onChange={(v) => handleChange('voiceInputMode', v)}
                options={[
                  { value: 'pushToTalk', label: 'Push to Talk' },
                  { value: 'voiceActivation', label: 'Voice Activation' },
                ]}
                data-testid="setting-voice-input-mode"
              />
            </SettingRow>
            {localSettings.voiceInputMode === 'voiceActivation' && (
              <SettingRow label="Voice Activation Sensitivity">
                <div className="flex items-center gap-2">
                  <Slider
                    value={localSettings.voiceActivationSensitivity}
                    onChange={(v) => handleChange('voiceActivationSensitivity', v)}
                  />
                  <span className="text-xs text-white/50 w-8">{Math.round(localSettings.voiceActivationSensitivity * 100)}%</span>
                </div>
              </SettingRow>
            )}
            <SettingRow label="Mute Microphone">
              <Toggle
                checked={localSettings.micMuted}
                onChange={(v) => handleChange('micMuted', v)}
              />
            </SettingRow>
          </SettingsSection>
          
          {/* Controls */}
          <SettingsSection title="Controls" icon={Gamepad2}>
            <button
              onClick={() => {
                onShowTutorial?.();
                onClose();
              }}
              className="w-full py-2 px-4 bg-amber-600 hover:bg-amber-500 rounded-lg text-white text-sm font-medium transition-colors"
            >
              Show Controls Tutorial
            </button>
          </SettingsSection>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-black/20">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
